import React, { useEffect, useState } from "react";
import { RCCButton, RCCGrid, RCCHidden, RCCIcon, RCCInput, RCCTooltip, arrayMove } from "react-common-components";
import InputNumber from "src/components/InputNumber";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import SortableContainer from "src/components/Sortable/Container";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { useFieldArray } from "react-hook-form";
import { v4 } from "uuid";
import formatNumber from "src/utils/formatNumber";
import stringToNumber from "src/utils/stringToNumber";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IComponentMilestoneItem } from "./interface";
import { styleAddItemButton } from "./styles";

const MilestoneItem: React.FC<IComponentMilestoneItem> = ({
	control,
	disabled,
	error,
	register,
	index,
	items
}) => {
	const { translate, currentLanguage } = useTranslation();
	const [acumulated, setAcumulated] = useState(0);
	const [percentsIncr, setPercentsIncr] = useState<number[]>([]);

	const { 
		fields: fieldsMilestonesItem, 
		remove: removeMilestonesItem,
		append: appendMilestonesItem,
		move: moveMilestonesItem
	} = useFieldArray({
		control,
		name: `oktbb002001[${index}].oktbb003`
	});
	
	const handleDragEndMilestonesItens = (activeId: string, overId: string) => {
		const activeIndex = fieldsMilestonesItem.findIndex((checksItems) => checksItems.id === activeId);
		const overIndex = fieldsMilestonesItem.findIndex((checksItems) => checksItems.id === overId);

		setPercentsIncr((prevState) => arrayMove(prevState, activeIndex, overIndex));

		moveMilestonesItem(activeIndex, overIndex);
	};

	const addItemToMilestone = () => {
		const milestoneItem: any = {};
		milestoneItem.id = disabled ? "disabled" : v4();
		milestoneItem.key = milestoneItem.id;
		milestoneItem.okb003Id= "0";
		milestoneItem.okb003Descricao = "";
		milestoneItem.okb003Verificado = "N";
		milestoneItem.okb003Percincr = 0;

		setPercentsIncr((prevState) => [...prevState, 0]);

		appendMilestonesItem(milestoneItem);
	};

	const removeItemMilestone = (idx: number) => {
		setPercentsIncr((prevState) => prevState.filter((_, i) => i !== idx));

		removeMilestonesItem(idx);
	};

	const onBlurMilestoneItemPercIncr = (e: React.FocusEvent<HTMLInputElement>, idx: number) => {
		const {value} = e.target;
		const updateState = [...percentsIncr];
		updateState[idx] = stringToNumber(value, currentLanguage);
		setPercentsIncr(updateState);
	};

	useEffect(() => {
		let sum = 0;
		percentsIncr.forEach((percent) => {
			sum += percent;
		});
		setAcumulated(sum);
	}, [percentsIncr]);

	useEffect(() => {
		setPercentsIncr(items.map((item) => item.okb003Percincr));
		appendMilestonesItem(items.map((item) => ({
			...item,
			id: disabled ? "disabled" : v4()
		})));
	}, []);

	return (
		<RCCGrid
			container
			item
			direction="column"
			style={{ position: "relative" }}
		>
			<RCCGrid styleInline={styleAddItemButton}>
				<RCCTooltip
					color="purple"
					text={translate("Adicionar Item")}				
				>
					<RCCButton
						color="transparent"
						icon={{
							name: "plus",
							color: "var(--purple)"
						}}
						onClick={() => addItemToMilestone()}
						disabled={disabled}
						tabIndex={-1}
					/>
				</RCCTooltip>
			</RCCGrid>
			{fieldsMilestonesItem && fieldsMilestonesItem.length > 0 && (
				<>
					<RCCGrid container item xs={12} sm={10} justifyContent="space-between">
						<RCCGrid container item xs={12} sm={7}>
							<Title color="purple" size="sm">
								{translate("Itens")}
							</Title>
						</RCCGrid>
						<RCCHidden only="xs">
							<RCCGrid container item sm={4}>
								<Title color="purple" size="sm">
									{translate("Incremento")} (%)
								</Title>
							</RCCGrid>
						</RCCHidden>
					</RCCGrid>
					<SortableContainer 
						items={fieldsMilestonesItem}
						onDragEnd={(activeId, overId) => handleDragEndMilestonesItens(activeId, overId)}
						modifiers={[restrictToVerticalAxis]}
					>	
						{fieldsMilestonesItem.map((itens: any, idx: number) => (
							<SortableItem id={itens.id} key={itens.key ? itens.key : `milestone-items-${itens.okb003Id}`} >
								<RCCGrid
									container
									item
									alignItems="center"
									justifyContent="space-between"
									
								>
									<input 
										type="text"
										value={itens.okb003Id}
										{...register(`oktbb002001[${index}].oktbb003[${idx}].okb003Id`)} 
										style={{ display: "none" }} 
										readOnly
										tabIndex={-1}
									/>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}	
										justifyContent="space-between"
										alignItems="center"
									>										
										<RCCGrid
											container
											item
											xs={12}
											sm={7}
											style={{ position: "relative" }}
										>
											<RCCInput 
												name={`oktbb002001[${index}].oktbb003[${idx}].okb003Descricao`}
												register={register}
												label=""
												maxLength={100}
												defaultValue={itens.okb003Descricao}
												error={error.oktbb003 && error.oktbb003[idx]?.okb003Descricao}
												disabled={disabled}
												styleInline="height: 34px;"
											/>
											{disabled && itens.okb003Verificado === "S" && (
												<RCCIcon 
													size={24}
													name="checkCircle"
													color="var(--green)"
													style={{ position: "absolute", right: "4px", bottom: "25%" }}
												/>
											)}
										</RCCGrid>
										<RCCGrid
											container
											item
											xs={12}
											sm={4}
										>
											<InputNumber
												name={`oktbb002001[${index}].oktbb003[${idx}].okb003Percincr`}
												label=""
												control={control}
												allowNegative={false}
												defaultValue={itens.okb003Percincr}
												error={error.oktbb003 && error.oktbb003[idx]?.okb003Percincr}
												onBlurCapture={(e) => onBlurMilestoneItemPercIncr(e, idx)}
												disabled={disabled}
												styleInline="height: 34px;"
											/>
										</RCCGrid>
									</RCCGrid>
									<RCCGrid
										container
										item
										justifyContent="flex-end"
										xs={12}
										sm={2}
									>
										<SortableHandle>
											<RCCTooltip
												color="grey"
												text={translate("Ordenar")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "arrows",
														color: "var(--grey)"
													}}
													layout="isRounded"
													disabled={disabled}
													tabIndex={-1}
												/>
											</RCCTooltip>
										</SortableHandle>

										<RCCTooltip
											color="red"
											text={translate("Excluir")}
										>
											<RCCButton
												color="transparent"
												icon={{
													name: "trash",
													color: "var(--red)"
												}}
												layout="isRounded"
												onClick={() => removeItemMilestone(idx)}
												disabled={disabled || fieldsMilestonesItem.length === 1}
												tabIndex={-1}
											/>
										</RCCTooltip>
									</RCCGrid>
                            
								</RCCGrid>
							</SortableItem>
						))}
					</SortableContainer>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					>
						<Title color="blue" size="md">
							{translate("Acumulado")}: {formatNumber(acumulated, currentLanguage)}
						</Title>
					</RCCGrid>
				</>
			)}
		</RCCGrid>
	);
};

export default MilestoneItem;
